/**
 * Created by jthomaskingslin on 2/15/2017.

 * source: http://www.levigross.com/2014/03/30/how-to-write-an-encrypt-and-decrypt-api-for-data-at-rest-in-nodejs/
 * modified with recommended way of key generation using pbkdf2Sync() and bcrypt methods.
 */
import { randomBytes, createCipheriv, createDecipheriv, pbkdf2Sync, createHmac as _createHmac } from "crypto";
import { gzipSync, gunzipSync } from "zlib";
var d = Symbol();
function error(message) {
    throw new Error(message);
}
/**
 * An encryption provider. Instances are created with options, and provides simple encryption and
 * decryption methods based on the provided options.
 *
 * @class SimpleEncryption
 */
var SimpleEncryption = /** @class */ (function () {
    /**
     * Creates an instance of SimpleEncryption.
     * @param {CryptoOptions} options
     */
    function SimpleEncryption(options) {
        this[d] = {
            algorithm: options.algorithm || "AES-128-CBC",
            keyAlgorithm: options.keyAlgorithm || "SHA256",
            secretKey: options.secretKey || error('options.secretKey must be provided'),
            hmacKey: options.hmacKey || error('options.hmacKey must be provided'),
            salt: options.salt || error("'options.salt' must be provided"),
            iterations: options.iterations || 10000,
            keyLength: options.keyLength || 16
        };
    }
    /**
    * Encript a string.
    *
    * @param {string} data A string to encrypt
    * @returns {string} Base64 encoded/encrypted string
    */
    SimpleEncryption.prototype.encrypt = function (data) {
        var _a = this[d], secretKey = _a.secretKey, algorithm = _a.algorithm;
        //generate derived key for secret.key
        var secretKeyBytes = getDerivedKey(this, secretKey);
        //generate derived key for random initialization vector
        var IVBytes = randomBytes(16);
        //encrypt
        var encryptor = createCipheriv(algorithm, secretKeyBytes, IVBytes);
        var cipherText = Buffer.concat([
            encryptor.update(gzipSync(Buffer.from(data, "utf8"))),
            encryptor.final()
        ]).toString("base64");
        //hmac to validate authenticity of cipher text
        var hmacObj = createHmac(this, cipherText, IVBytes);
        return cipherText + "$" + IVBytes.toString('base64') + "$" + hmacObj.digest('hex');
    };
    /**
     * Dectypt an ecrypted string
     *
     * @param {string} cipher The encrypted string
     * @returns {string} The unencrypted string
     */
    SimpleEncryption.prototype.decrypt = function (cipher) {
        if (typeof cipher !== 'string' || cipher.length === 0) {
            throw new Error('Input invalid');
        }
        var _a = this[d], algorithm = _a.algorithm, secretKey = _a.secretKey;
        var cipherArray = cipher.split("$");
        var cipherText = cipherArray[0];
        var IVBytes = Buffer.from(cipherArray[1], 'base64');
        var HMACInput = cipherArray[2];
        var hmacObj = createHmac(this, cipherText, IVBytes);
        //validate authenticity of cipher text using constant-time function
        if (!constantTimeCompare(hmacObj.digest('hex'), HMACInput)) {
            throw new Error("Encrypted content has been tampered");
        }
        //decrypt the cipher
        var decryptor = createDecipheriv(algorithm, getDerivedKey(this, secretKey), IVBytes);
        var decryptedBuffer = Buffer.concat([
            decryptor.update(Buffer.from(cipherText, "base64")),
            decryptor.final()
        ]);
        var decryptedText = gunzipSync(decryptedBuffer).toString("utf8");
        return decryptedText;
    };
    return SimpleEncryption;
}());
export { SimpleEncryption };
function getDerivedKey(context, key) {
    var _a = context[d], secretKey = _a.secretKey, salt = _a.salt, iterations = _a.iterations, keyLength = _a.keyLength, keyAlgorithm = _a.keyAlgorithm;
    //generate derived key for i/p key using configured salt
    //using pbkdf2 => Password-Based Key Derivation Function 2
    return pbkdf2Sync(key, salt, iterations, keyLength, keyAlgorithm);
}
function createHmac(context, data, iv) {
    var _a = context[d], keyAlgorithm = _a.keyAlgorithm, hmacKey = _a.hmacKey;
    var hmacObj = _createHmac(keyAlgorithm, hmacKey);
    hmacObj.update(data);
    hmacObj.update(iv.toString('base64'));
    return hmacObj;
}
//constant time fn. to avoid timing attack
function constantTimeCompare(val1, val2) {
    var sentinel;
    if (val1.length !== val2.length) {
        return false;
    }
    for (var i = 0; i <= (val1.length - 1); i++) {
        sentinel |= val1.charCodeAt(i) ^ val2.charCodeAt(i);
    }
    return sentinel === 0;
}
/**
 * Create an instance of SimpleEncryption using provided options.
 *
 * @export
 * @param {CryptoOptions} options The options for the encryption provider
 * @returns {SimpleEncryption} An instance of SimpleEncryption
 */
export default function simpleEncryption(options) {
    return new SimpleEncryption(options);
}
//# sourceMappingURL=simple-encryption.js.map